import { mergeStyleSets, Stack, StackItem } from '@fluentui/react'
import { mergeClasses, Text, Tooltip } from '@fluentui/react-components'
import * as React from 'react'
import { ReactElement, useEffect } from 'react'
import { Currency, MoneyDto } from '../../../config/i18nConfig'
import { formatDecimals } from '../../../helpers/numberHelper'
import useCurrency from '../../../hooks/useCurrency'
import { TextProps } from '@fluentui/react-text'

type Props = {
    value?: MoneyDto
    amount?: number | string
    textProps?: TextProps
    currency?: Currency
    alpha?: string
    className?: string
    align?: 'left' | 'center' | 'right' | 'spaceBetween'
    inline?: boolean
    tooltip?: string
    hideCurrency?: boolean
}

const classNames = mergeStyleSets({
    root: { whiteSpace: 'nowrap', color: 'inherit' },
    inline: { display: 'inline-flex' },
    leftAlign: { justifyContent: 'left' },
    centerAlign: { justifyContent: 'center' },
    rightAlign: { justifyContent: 'right' },
    spaceBetween: { justifyContent: 'space-between' },
})

const AlignmentMap = new Map([
    ['left', classNames.leftAlign],
    ['center', classNames.centerAlign],
    ['right', classNames.rightAlign],
    ['spaceBetween', classNames.spaceBetween],
])

function MoneyTooltip(props: Props & { child: any }): ReactElement {
    if (!props.tooltip) {
        return props.child
    }

    return (
        <Tooltip relationship={'description'} content={props.tooltip} withArrow>
            {props.child}
        </Tooltip>
    )
}

export default function MoneyText(props: Props): ReactElement {
    const { currency, setAlpha: setCurrency } = useCurrency(props.value?.currency || props.alpha, props.currency)

    useEffect(() => {
        const alpha = props.value?.currency || props.currency?.alpha || props.alpha
        if (alpha !== currency.alpha) {
            setCurrency(alpha)
        }
    }, [props.value?.currency, props.currency?.alpha, props.alpha])

    if (props.value === undefined && props.amount === undefined) {
        return <></>
    }

    const decimals: number = currency?.decimals || 2
    const symbol: string = currency?.symbol || currency?.alpha || '¤'
    const amount: string = formatDecimals(props.value?.amount || props.amount, decimals, 'en', 'nl-nl')

    const integerPart = decimals !== 0 ? amount.slice(0, (decimals + 1) * -1) : amount
    const fractionalPart = decimals !== 0 && amount.slice((decimals + 1) * -1)

    return (
        <Stack
            horizontal
            tokens={{ childrenGap: 's2' }}
            className={mergeClasses(classNames.root, props.inline && classNames.inline, AlignmentMap.get(props.align || 'left'), props.className)}
        >
            {props.hideCurrency !== true && (
                <StackItem className={classNames.root}>
                    <Text {...props.textProps} className={mergeClasses(classNames.root, props.textProps?.className)}>
                        {symbol}
                    </Text>
                </StackItem>
            )}
            <StackItem className={classNames.root}>
                <MoneyTooltip
                    {...props}
                    child={
                        <Text {...props.textProps} className={mergeClasses(classNames.root, props.textProps?.className)}>
                            {integerPart}
                            {fractionalPart && <small>{fractionalPart}</small>}
                        </Text>
                    }
                ></MoneyTooltip>
            </StackItem>
        </Stack>
    )
}
